import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getCompanyLeaveSummary, getEmployeeEntitlements, generateDefaultEntitlements, batchUpdateEntitlements,
} from "../../services/leaveEntitlementService";
import { getCustomFields } from "../../services/customFieldService";
import {
  getTimeOffTypes, getTimeOffRequests, createTimeOffRequest, updateTimeOffRequest, batchUpdateTimeOffRequests,
} from "../../services/timeOffService";
import { getEmployees } from "../../services/peopleService";
import { useCurrentUser } from "../../context/UserContext";
import { usePermissions } from "../../hooks/usePermission";
import { usePendingApprovals } from "../../context/PendingApprovalsContext";
import { useCurrency } from "../../hooks/useCurrency";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import useTableColumns from "../../hooks/useTableColumns";
import useSelection from "../../hooks/useSelection";
import useBatchRunner from "../../hooks/useBatchRunner";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../components/ConfirmDialog";
import FormModal from "../../components/forms/FormModal";
import DataTable from "../../components/forms/DataTable";
import CustomDropdown from "../../components/forms/CustomDropdown";
import SegmentedControl from "../../components/common/SegmentedControl";
import RowActionsMenu from "../../components/common/RowActionsMenu";
import BatchBar from "../../components/common/BatchBar";
import Avatar from "../../components/common/Avatar";
import CustomFieldRows from "../../components/common/CustomFieldRows";
import CustomFieldModal from "../../components/settings/CustomFieldModal";
import chrome from "../../components/common/PageChrome.module.css";
import ui from "./DashboardUI.module.css";
import styles from "./leave/LeaveUI.module.css";
import LeaveCalendar from "./leave/LeaveCalendar";
import {
  BALANCE_BASE_COLUMNS, BALANCE_GROUPS, DEFAULT_BALANCE_FILTERS, DEFAULT_REQUEST_FILTERS, REQUEST_COLUMNS, REQUEST_GROUPS,
  STATUS_ORDER, STATUS_STYLE, balanceAccessors, balanceAfter, balanceByKey, balanceExportColumns, balanceFields, buildBalances,
  filterBalances, filterRequests, monthLabel, normalizeRequest, offToday, requestExportColumns, requestPreview, shiftMonth,
  sortRequests, typeColor, typeIdOf,
} from "./leave/leaveConfig";
import { errorMessage } from "../../utils/errorMessage";
import { formatDays } from "../../utils/leaveDays";
import { buildTimeOffCreatePayload, buildTimeOffUpdatePayload } from "../../utils/timeOffForm";
import { sortByAccessor, toggleSort } from "../../utils/sortRows";
import ExportDialog from "../../components/common/ExportDialog";
import { toISODate } from "../../utils/weekDates";
import { daysUntil } from "../../components/projects/dueDates";
import { customCellRenderers } from "../../utils/customFieldUtils";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const NO_FIELDS = [];
const shortDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const blankForm = (employeeId, typeId, today) => ({
  timeOffId: null, employee_id: String(employeeId || ""), timeOffTypeId: String(typeId || ""), startDate: today, endDate: today,
  startHalfDay: false, endHalfDay: false, reason: "", request: null, custom_fields: {},
});
const yearRange = (y) => ({ period_start: `${y}-01-01`, period_end: `${y}-12-31` });

export default function LeaveManager() {
  const currentUser = useCurrentUser();
  const {
    canApproveTimeOff, canEditPeople, canViewPeople, canCreateOwnTimeOff, canCreateTeamTimeOff, canEditOwnPendingTimeOff,
    canManageCompanySettings,
  } = usePermissions();
  const { format: formatMoney } = useCurrency();
  const { refreshPendingCount } = usePendingApprovals();
  const canViewBalances = canApproveTimeOff || canViewPeople;
  const canRequest = canApproveTimeOff || canCreateOwnTimeOff || canCreateTeamTimeOff;
  const today = toISODate(new Date());
  const year = new Date().getFullYear();

  const selfId = currentUser?.employee_id;
  const [requests, setRequests] = useState([]);
  // Current-year balances feed the previews; the Balances tab may be looking at another year.
  const [summary, setSummary] = useState([]);
  const [yearSummary, setYearSummary] = useState(null);
  const [balanceYear, setBalanceYear] = useState(year);
  const [types, setTypes] = useState([]);
  const [requestFields, setRequestFields] = useState([]);
  const [addDetail, setAddDetail] = useState(false);
  // Only recent leave is loaded; "Load earlier" moves this back a year at a time.
  const since = useRef(`${year - 1}-01-01`);
  const [loaded, setLoaded] = useState(false);
  const showLoading = useMinimumLoader(!loaded);
  const [toast, setToast] = useState(null);
  const runBatch = useBatchRunner(setToast);

  const [savedTab, setTab] = useState(() => {
    try { return localStorage.getItem("leaveTab"); } catch { return null; }
  });
  const tab = savedTab === "balances" && canViewBalances ? "balances" : "requests";
  useEffect(() => {
    try { localStorage.setItem("leaveTab", tab); } catch { /* remembering the tab is optional */ }
  }, [tab]);
  const [mode, setMode] = useState("list");
  const [month, setMonth] = useState(today.slice(0, 7));
  const [filters, setFilters] = useState({ requests: DEFAULT_REQUEST_FILTERS, balances: DEFAULT_BALANCE_FILTERS });
  const [collapsed, setCollapsed] = useState({ requests: {}, balances: {} });
  const [form, setForm] = useState(null);
  const [formTab, setFormTab] = useState("details");
  const [daysForm, setDaysForm] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // Both field lists stay empty until loaded so stored column choices aren't pruned early.
  const customFields = loaded ? requestFields : NO_FIELDS;
  const requestColumns = useTableColumns("leaveRequestColumns", REQUEST_COLUMNS, customFields);
  const fields = useMemo(() => (loaded ? balanceFields(types) : NO_FIELDS), [loaded, types]);
  const balanceColumns = useTableColumns("leaveBalanceColumns", BALANCE_BASE_COLUMNS, fields);
  const selections = { requests: useSelection(), balances: useSelection() };
  const selection = selections[tab];

  const load = useCallback(async () => {
    // Managers see everyone's balances; anyone else only their own, enough for the request preview.
    const balancesFor = (y) => (canViewBalances ? getCompanyLeaveSummary(yearRange(y)) : selfId ? getEmployeeEntitlements(selfId, yearRange(y)) : []);
    const [reqs, sum, other, ty, cf] = await Promise.all([
      getTimeOffRequests({ ends_after: since.current }),
      balancesFor(year),
      canViewBalances && balanceYear !== year ? balancesFor(balanceYear) : null,
      getTimeOffTypes(),
      getCustomFields("leave_request").catch(() => ({ fields: [] })),
    ]);
    setRequests((reqs || []).map(normalizeRequest));
    setSummary(sum || []);
    setYearSummary(other);
    setTypes(ty || []);
    setRequestFields(cf.fields || []);
    return (reqs || []).length;
  }, [canViewBalances, selfId, year, balanceYear]);

  useEffect(() => {
    if (!currentUser) return;
    load()
      .catch((err) => setToast({ message: errorMessage(err, "Failed to load leave."), type: "error" }))
      .finally(() => setLoaded(true));
  }, [currentUser, load]);

  const refresh = useCallback(async () => {
    await load();
    refreshPendingCount();
  }, [load, refreshPendingCount]);

  const goMonth = (n) => {
    const next = shiftMonth(month, n);
    setMonth(next);
    loadEarlier(`${next}-01`);
  };

  const loadEarlier = async (date) => {
    if (date >= since.current) return;
    since.current = `${date.slice(0, 4)}-01-01`;
    try {
      const before = requests.length;
      if ((await load()) === before) showToast(`No leave found before ${monthLabel(date)}.`);
    } catch (err) {
      showToast(errorMessage(err, "Failed to load earlier leave."), "error");
    }
  };

  const balances = useMemo(() => buildBalances(yearSummary || summary), [yearSummary, summary]);
  const balanceMap = useMemo(() => balanceByKey(summary), [summary]);
  const typeById = useMemo(() => Object.fromEntries(types.map((t) => [typeIdOf(t), t])), [types]);
  const people = useMemo(() => {
    const byId = new Map(balances.map((b) => [b.id, { id: b.id, name: b.name, avatar: b.avatar }]));
    requests.forEach((r) => {
      if (!byId.has(r.employee_id)) byId.set(r.employee_id, { id: r.employee_id, name: r.employee_name, avatar: r.employee_avatar });
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [balances, requests]);

  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const off = offToday(requests, today);

  const f = filters[tab];
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, [tab]: { ...prev[tab], ...patch } }));
  const clearFilters = () => setFilters((prev) => ({ ...prev, [tab]: tab === "requests" ? DEFAULT_REQUEST_FILTERS : DEFAULT_BALANCE_FILTERS }));

  const calendar = tab === "requests" && mode === "calendar";
  const requestRows = sortRequests(filterRequests(requests, calendar ? { ...f, period: "all" } : f, today), f.sort, f.period, customFields);
  const balanceRows = (() => {
    const filtered = filterBalances(balances, f);
    return f.sort ? sortByAccessor(filtered, f.sort, balanceAccessors(types)[f.sort.key]) : filtered;
  })();
  const query = f.search.trim().toLowerCase();
  const calendarPeople = people.filter((p) => (!f.employee || String(p.id) === f.employee) && (!query || p.name.toLowerCase().includes(query)));

  const rows = tab === "requests" ? requestRows : balanceRows;
  const columnState = tab === "requests" ? requestColumns : balanceColumns;
  const shownColumns = columnState.columns;
  const groups = tab === "requests" ? REQUEST_GROUPS : BALANCE_GROUPS;
  const group = groups[f.groupBy] || null;
  const selectedRows = rows.filter((r) => selection.selectedIds.has(r.id));
  const showToast = (message, type = "success") => setToast({ message, type });

  // ---- Decisions and batch changes (each offers Undo) ----
  const decide = (ids, status) => runBatch(
    () => batchUpdateTimeOffRequests({ ids, status }),
    refresh,
    (res) => (res.updated.length ? `${plural(res.updated.length, "request", "requests")} ${status}.` : "Nothing to change."),
    (res) => (res.updated.length ? batchUpdateTimeOffRequests({ restore: res.previous }) : null),
  );

  const decideSelected = (status) => {
    const ids = selectedRows.map((r) => r.id);
    selection.clear();
    decide(ids, status);
  };

  const changeDays = (e) => {
    e.preventDefault();
    const days = Number(daysForm.days);
    if (!Number.isFinite(days) || (daysForm.mode === "carry" ? days < 0 : days === 0)) return showToast("Enter a number of days.", "error");
    const ids = selectedRows.map((r) => r.id);
    const { mode: kind, typeId, year: forYear } = daysForm;
    setDaysForm(null);
    selection.clear();
    runBatch(
      () => batchUpdateEntitlements({
        employee_ids: ids,
        time_off_type_id: Number(typeId),
        ...yearRange(forYear),
        ...(kind === "add" ? { total_days_delta: days } : { carry_over_days: days }),
      }),
      refresh,
      (res) => `${kind === "add" ? `${days > 0 ? "+" : ""}${formatDays(days)} days for` : `Carry over set to ${formatDays(days)} for`} ${plural(res.updated.length, "person", "people")}${res.skipped ? `; ${res.skipped} had no ${forYear} entitlement` : ""}.`,
      (res) => (res.updated.length ? batchUpdateEntitlements({ restore: res.previous }) : null),
    );
  };

  const generateDefaults = () => setConfirm({
    message: `Create ${balanceYear} default entitlements for everyone who doesn't have them yet?`,
    confirmText: "Generate",
    onConfirm: async () => {
      setConfirm(null);
      try {
        const active = ((await getEmployees())?.employees || []).filter((e) => e.status === "active");
        // The employee list names each person's employee id user_id.
        const results = await Promise.allSettled(active.map((e) => generateDefaultEntitlements(e.user_id, yearRange(balanceYear))));
        const failed = results.filter((r) => r.status === "rejected").length;
        await refresh();
        showToast(`Defaults generated for ${plural(active.length - failed, "person", "people")}${failed ? `; ${failed} failed` : ""}.`, failed ? "error" : "success");
      } catch (err) {
        showToast(errorMessage(err, "Failed to generate defaults."), "error");
      }
    },
  });

  // ---- Export ----
  const [exporting, setExporting] = useState(null);
  const exportName = tab === "requests" ? "leave-requests" : "leave-balances";

  // ---- Request form ----
  const openNew = () => setForm(blankForm(selfId, typeIdOf(types[0] || {}) || "", today));
  const openRequest = (r) => {
    setFormTab("details");
    setForm({
      timeOffId: r.id, employee_id: String(r.employee_id), timeOffTypeId: String(r.type_id), startDate: r.start, endDate: r.end,
      startHalfDay: !!r.start_half_day, endHalfDay: !!r.end_half_day, reason: r.reason || "", request: r, custom_fields: r.custom_fields,
    });
  };
  const closeForm = () => { setForm(null); setFormTab("details"); };

  const editable = form && (!form.timeOffId
    || canApproveTimeOff
    || (canEditOwnPendingTimeOff && form.request.status === "pending" && String(form.request.employee_id) === String(selfId)));

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.timeOffTypeId || !form.startDate || !form.endDate) return showToast("Please fill in all required fields.", "error");
    if (form.endDate < form.startDate) return showToast("The end date can't be before the start date.", "error");
    try {
      if (form.timeOffId) await updateTimeOffRequest(form.timeOffId, { ...buildTimeOffUpdatePayload(form), custom_fields: form.custom_fields });
      else await createTimeOffRequest({ ...buildTimeOffCreatePayload(form, selfId), custom_fields: form.custom_fields });
      const wasNew = !form.timeOffId;
      closeForm();
      await refresh();
      showToast(wasNew ? "Leave requested." : "Request updated.");
    } catch (err) {
      showToast(errorMessage(err, "Failed to save the request."), "error");
    }
  };

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const personOptions = people.map((p) => ({ value: String(p.id), label: p.name }));

  const renderDates = () => {
    const sameDay = form.startDate && form.startDate === form.endDate;
    return (
      <div>
        <div className={styles.dates}>
          <div>
            <label htmlFor="leave-start">Start date</label>
            <input id="leave-start" type="date" value={form.startDate} disabled={!editable} onChange={(e) => set({ startDate: e.target.value })} />
          </div>
          <div>
            <label htmlFor="leave-end">End date</label>
            <input id="leave-end" type="date" value={form.endDate} disabled={!editable} onChange={(e) => set({ endDate: e.target.value })} />
          </div>
        </div>
        <div className={styles.halfDays}>
          <label>
            <input type="checkbox" checked={form.startHalfDay} disabled={!editable} onChange={(e) => set({ startHalfDay: e.target.checked })} />
            {sameDay ? "Half day" : "First day is a half-day"}
          </label>
          {!sameDay && (
            <label>
              <input type="checkbox" checked={form.endHalfDay} disabled={!editable} onChange={(e) => set({ endHalfDay: e.target.checked })} />
              Last day is a half-day
            </label>
          )}
        </div>
      </div>
    );
  };

  const renderChecks = () => {
    if (!form.startDate || !form.endDate || form.endDate < form.startDate) return null;
    const { days, left, after, others } = requestPreview({ form, balanceMap, requests, year });
    const typeName = typeById[Number(form.timeOffTypeId)]?.name || "Leave";
    return (
      <div className={styles.checks} aria-live="polite">
        <div className={styles.checksRow}>
          <span>Working days</span>
          <strong>{formatDays(days)} {days === 1 ? "day" : "days"}</strong>
        </div>
        {left != null && (
          <div className={styles.checksRow}>
            <span>{typeName} balance</span>
            <span><b>{formatDays(left)}</b> left → <b className={after < 0 ? styles.over : styles.good}>{formatDays(after)}</b> after</span>
          </div>
        )}
        {after != null && after < 0 && (
          <div className={`${styles.checksWarn} ${styles.checksOver}`}>This is {formatDays(-after)} {-after === 1 ? "day" : "days"} more than their {typeName.toLowerCase()} balance.</div>
        )}
        {others.length > 0 && <div className={styles.checksWarn}>Also off during these dates: {others.join(", ")}.</div>}
      </div>
    );
  };

  const request = form?.request;
  const formFields = form ? [
    ...(canApproveTimeOff || canCreateTeamTimeOff ? [{
      label: "Employee", type: "select", value: form.employee_id, disabled: Boolean(form.timeOffId),
      onChange: (e) => set({ employee_id: e.target.value }), options: personOptions,
    }] : []),
    {
      label: "Leave type", type: "select", value: form.timeOffTypeId, disabled: !editable,
      onChange: (e) => set({ timeOffTypeId: e.target.value }),
      options: types.map((t) => ({ value: String(typeIdOf(t)), label: t.name })),
    },
    { label: "Dates", type: "custom", render: renderDates },
    { type: "custom", render: renderChecks },
    { label: "Note", type: "textarea", value: form.reason, disabled: !editable, onChange: (e) => set({ reason: e.target.value }) },
    ...(customFields.length ? [{
      type: "custom",
      render: () => (
        <CustomFieldRows
          fields={customFields}
          entity={form}
          disabled={!editable}
          formatMoney={formatMoney}
          onSave={(field, value) => set({ custom_fields: { ...form.custom_fields, [field.field_id]: value } })}
        />
      ),
    }] : []),
  ] : [];

  const activity = request ? (
    <ul className={styles.activity}>
      <li>Requested{request.submitted ? ` on ${shortDate(request.submitted)}` : ""}<small>{request.employee_name}</small></li>
      {request.status !== "pending" && (
        <li>
          {request.statusLabel}{request.approved_by_name ? ` by ${request.approved_by_name}` : ""}
          <small>{shortDate(toISODate(new Date(request.updated_at)))}</small>
        </li>
      )}
    </ul>
  ) : null;

  const footerActions = request?.status === "pending" && canApproveTimeOff ? (
    <span className={styles.footerButtons}>
      <button type="button" className={ui.buttonPrimary} onClick={() => { closeForm(); decide([request.id], "approved"); }}>Approve</button>
      <button type="button" className={ui.buttonDanger} onClick={() => { closeForm(); decide([request.id], "rejected"); }}>Reject</button>
    </span>
  ) : null;

  // ---- Cells ----
  const relative = (r) => {
    if (r.start <= today && today <= r.end) return r.status === "approved" ? "Off today" : "Today";
    const until = daysUntil(r.start);
    return until > 0 ? `In ${plural(until, "day", "days")}` : `${plural(-daysUntil(r.end), "day", "days")} ago`;
  };
  const nameCell = (name, avatar) => (
    <span className={chrome.nameCell}>
      <Avatar name={name} imageUrl={avatar} size="sm" round />
      <span>{name}</span>
    </span>
  );

  const requestCells = {
    employee: (r) => nameCell(r.employee_name, r.employee_avatar),
    type: (r) => <span className={styles.typePill} style={{ "--dot": typeColor(typeById[r.type_id], types) }}>{r.type_name}</span>,
    dates: (r) => (
      <span>
        {r.start === r.end ? shortDate(r.start) : `${shortDate(r.start)} – ${shortDate(r.end)}`}
        <span className={chrome.cellSub}>{relative(r)}</span>
      </span>
    ),
    days: (r) => {
      const after = r.status === "pending" ? balanceAfter(r, balanceMap, year) : null;
      return (
        <span>
          {formatDays(r.days)} {r.days === 1 ? "day" : "days"}
          {after != null && (after < 0
            ? <span className={`${chrome.cellSub} ${styles.over}`}>{formatDays(-after)} over</span>
            : <span className={chrome.cellSub}>{formatDays(after)} left after</span>)}
        </span>
      );
    },
    status: (r) => {
      const { bg, fg } = STATUS_STYLE[r.statusLabel] || STATUS_STYLE.Cancelled;
      return (
        <span className={styles.statusCell} onClick={(e) => e.stopPropagation()}>
          <span className={styles.statusPill} style={{ "--pill-bg": bg, "--pill-fg": fg }}>{r.statusLabel}</span>
          {r.status === "pending" && canApproveTimeOff && (
            <span className={styles.decide}>
              <button type="button" className={`${styles.decideButton} ${styles.approve}`} aria-label={`Approve ${r.employee_name}'s request`} onClick={() => decide([r.id], "approved")}>✓</button>
              <button type="button" className={`${styles.decideButton} ${styles.reject}`} aria-label={`Reject ${r.employee_name}'s request`} onClick={() => decide([r.id], "rejected")}>✕</button>
            </span>
          )}
        </span>
      );
    },
    submitted: (r) => (r.submitted ? shortDate(r.submitted) : "—"),
    note: (r) => r.reason || "—",
    approved_by: (r) => r.approved_by_name || "—",
    department: (r) => r.department || "—",
    ...customCellRenderers(customFields, formatMoney),
  };

  const balanceCells = {
    employee: (b) => nameCell(b.name, b.avatar),
    cf_pending: (b) => (b.pending ? <span className={styles.warn}>{formatDays(b.pending)} days</span> : "—"),
    cf_remaining: (b) => <span className={b.remaining <= 2 ? styles.low : styles.good}>{formatDays(b.remaining)} days</span>,
    cf_carry: (b) => (b.carry ? formatDays(b.carry) : "—"),
    cf_department: (b) => b.department || "—",
    ...Object.fromEntries(types.map((t) => [`cf_${typeIdOf(t)}`, (b) => {
      const e = b.byType[typeIdOf(t)];
      if (!e) return "—";
      const used = Number(e.days_approved);
      const total = Number(e.total_available);
      return (
        <span className={styles.usage}>
          <span className={styles.usageText}>{formatDays(used)} / {formatDays(total)}</span>
          <span className={styles.track}><span className={styles.fill} style={{ width: `${total > 0 ? Math.min(100, (used / total) * 100) : 0}%`, "--bar": typeColor(t, types) }} /></span>
        </span>
      );
    }])),
  };

  const departments = [...new Set(balances.map((b) => b.department).filter(Boolean))].sort();
  const employeeOptions = [{ value: "", label: "All Employees" }, ...people.map((p) => ({ value: String(p.id), label: p.name }))];
  const typeSelect = types.map((t) => ({ value: String(typeIdOf(t)), label: t.name }));
  const typeOptions = [{ value: "", label: "All Types" }, ...typeSelect];
  const groupOptions = [{ value: "", label: "No Grouping" }, ...Object.entries(groups).map(([value, g]) => ({ value, label: `Group by ${g.label}` }))];
  const yearOptions = [year - 1, year, year + 1].map((y) => ({ value: String(y), label: String(y) }));

  if (showLoading) {
    return (
      <div className={ui.pageContainer}>
        <div className={ui.toolbarSkeleton}>
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.toolbarSkeletonPill}`} />)}
        </div>
        <div className={ui.tableSkeleton}>
          <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />)}
        </div>
      </div>
    );
  }

  const noun = tab === "requests" ? "requests" : "balances";
  const menuActions = [
    { label: "Export…", onClick: () => setExporting("filtered") },
    ...(tab === "balances" && canEditPeople ? [{ label: "Generate defaults for everyone", onClick: generateDefaults }] : []),
  ];

  return (
    <div className={ui.fadeInContent}>
      {toast && <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => setToast(null)} />}

      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft} style={{ alignItems: "center" }}>
          <h1 className={ui.pageTitle}>Leave</h1>
          <span className={styles.summary}>{pendingCount} pending · {off} off today</span>
          {canViewBalances && (
            <SegmentedControl
              ariaLabel="Requests or balances"
              options={[{ value: "requests", label: "Requests", count: pendingCount }, { value: "balances", label: "Balances" }]}
              value={tab}
              onChange={setTab}
            />
          )}
        </div>
        <div className={ui.pageHeaderRight}>
          {tab === "requests" && (
            <SegmentedControl
              ariaLabel="List or calendar"
              options={[{ value: "list", label: "List" }, { value: "calendar", label: "Calendar" }]}
              value={mode}
              onChange={setMode}
            />
          )}
          <RowActionsMenu actions={menuActions} />
          {canRequest && types.length > 0 && (
            <button type="button" className={ui.addButton} onClick={openNew}>
              <span className={ui.icon}>＋</span> Request Leave
            </button>
          )}
        </div>
      </div>

      <div className={ui.tableToolbar}>
        <input
          type="text"
          className={ui.toolbarSearch}
          placeholder="Search people…"
          aria-label="Search people"
          value={f.search}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        {tab === "requests" ? (
          <>
            {canApproveTimeOff && <CustomDropdown toolbar options={employeeOptions} value={f.employee} onChange={(v) => setFilter({ employee: v })} />}
            <CustomDropdown toolbar options={typeOptions} value={f.type} onChange={(v) => setFilter({ type: v })} />
            {!calendar && <CustomDropdown toolbar options={groupOptions} value={f.groupBy} onChange={(v) => setFilter({ groupBy: v })} />}
            {!calendar && (
              <SegmentedControl
                ariaLabel="Upcoming or past"
                options={[{ value: "upcoming", label: "Upcoming" }, { value: "past", label: "Past" }]}
                value={f.period}
                onChange={(v) => setFilter({ period: v })}
              />
            )}
            {calendar && (
              <span className={styles.calPager}>
                <button type="button" className={ui.toolbarReset} aria-label="Previous month" onClick={() => goMonth(-1)}>‹</button>
                <span className={styles.calMonth}>{monthLabel(`${month}-01`)}</span>
                <button type="button" className={ui.toolbarReset} aria-label="Next month" onClick={() => goMonth(1)}>›</button>
              </span>
            )}
          </>
        ) : (
          <>
            <CustomDropdown toolbar options={yearOptions} value={String(balanceYear)} onChange={(v) => setBalanceYear(Number(v))} />
            <CustomDropdown toolbar options={[{ value: "", label: "All Departments" }, ...departments.map((d) => ({ value: d, label: d }))]} value={f.department} onChange={(v) => setFilter({ department: v })} />
            <CustomDropdown toolbar options={groupOptions} value={f.groupBy} onChange={(v) => setFilter({ groupBy: v })} />
          </>
        )}
        <button type="button" className={ui.toolbarReset} onClick={clearFilters}>Clear</button>
      </div>

      {!calendar && (
        <BatchBar count={selection.selectedIds.size} onClear={selection.clear}>
          {tab === "requests" && canApproveTimeOff && (
            <>
              <button type="button" onClick={() => decideSelected("approved")}>Approve</button>
              <button type="button" onClick={() => decideSelected("rejected")}>Reject</button>
            </>
          )}
          {tab === "balances" && canEditPeople && (
            <>
              <button type="button" onClick={() => setDaysForm({ mode: "add", typeId: String(typeIdOf(types[0] || {})), days: "", year: String(balanceYear) })}>Add days…</button>
              <button type="button" onClick={() => setDaysForm({ mode: "carry", typeId: String(typeIdOf(types[0] || {})), days: "", year: String(balanceYear) })}>Set carry over…</button>
            </>
          )}
          <button type="button" onClick={() => setExporting("selected")}>Export</button>
        </BatchBar>
      )}

      {calendar ? (
        <LeaveCalendar month={month} people={calendarPeople} requests={requestRows} types={types} onOpen={openRequest} />
      ) : rows.length === 0 ? (
        <p style={{ padding: "24px 20px", color: "#64748b" }}>
          {(tab === "requests" ? requests : balances).length === 0 ? `No ${noun} yet.` : `No ${noun} match these filters.`}
        </p>
      ) : (
        <DataTable
          key={tab}
          columns={shownColumns}
          data={rows}
          storageKey={tab === "requests" ? "leaveRequestColumns" : "leaveBalanceColumns"}
          groupedBy={group ? group.get : null}
          groupOrder={f.groupBy === "status" ? STATUS_ORDER : []}
          getGroupColor={(name) => STATUS_STYLE[name]?.dot || "#85B7EB"}
          renderGroupExtra={tab === "requests" ? (grouped) => (
            <span className={styles.groupDays}>{formatDays(grouped.reduce((s, r) => s + r.days, 0))} days</span>
          ) : null}
          collapsedGroups={collapsed[tab]}
          toggleGroupCollapse={(g) => setCollapsed((prev) => ({ ...prev, [tab]: { ...prev[tab], [g]: !prev[tab][g] } }))}
          sortColumn={f.sort?.key}
          sortDirection={f.sort?.dir || "asc"}
          onSort={(key) => setFilter({ sort: toggleSort(f.sort, key) })}
          onRowClick={tab === "requests" ? openRequest : undefined}
          onToggleColumn={columnState.toggleColumn}
          onMoveColumn={columnState.moveColumn}
          onAddDetail={tab === "requests" && canManageCompanySettings ? () => setAddDetail(true) : null}
          selectable={tab === "requests" ? canApproveTimeOff : canEditPeople}
          selectedIds={selection.selectedIds}
          getRowId={(row) => row.id}
          onToggleRow={selection.toggleRow}
          onToggleGroup={selection.toggleGroup}
          onToggleAll={(checked) => selection.toggleAll(rows.map((r) => r.id), checked)}
          customCellRender={tab === "requests" ? requestCells : balanceCells}
        />
      )}

      {tab === "requests" && !calendar && f.period === "past" && (
        <p className={styles.earlier}>
          Showing leave from {monthLabel(since.current)}.{" "}
          <button type="button" className={ui.toolbarReset} onClick={() => loadEarlier(`${Number(since.current.slice(0, 4)) - 1}-01-01`)}>Load earlier</button>
        </p>
      )}

      {addDetail && (
        <CustomFieldModal
          defaultEntity="leave_request"
          onClose={() => setAddDetail(false)}
          onSaved={() => load().catch((err) => showToast(errorMessage(err, "Failed to reload."), "error"))}
          onMessage={setToast}
        />
      )}

      {form && (
        <FormModal
          title={!form.timeOffId ? "Request Leave" : editable ? "Edit Leave Request" : "Leave Request"}
          onCancel={closeForm}
          onSubmit={formTab === "details" ? submitForm : null}
          overviewReadOnly={!editable}
          submitLabel={form.timeOffId ? "Save" : "Submit request"}
          tabs={form.timeOffId ? [{ key: "details", label: "Details" }, { key: "activity", label: "Activity" }] : null}
          activeTab={form.timeOffId ? formTab : null}
          onTabChange={setFormTab}
          customTabContent={formTab === "activity" && form.timeOffId ? activity : null}
          footerActions={footerActions}
          fields={formFields}
        />
      )}

      {daysForm && (
        <FormModal
          title={daysForm.mode === "add" ? "Add Days" : "Set Carry Over"}
          onCancel={() => setDaysForm(null)}
          onSubmit={changeDays}
          submitLabel="Apply"
          fields={[
            { label: "Leave type", type: "select", value: daysForm.typeId, options: typeSelect, onChange: (e) => setDaysForm((p) => ({ ...p, typeId: e.target.value })) },
            { label: "Year", type: "select", value: daysForm.year, options: yearOptions, onChange: (e) => setDaysForm((p) => ({ ...p, year: e.target.value })) },
            {
              label: daysForm.mode === "add" ? "Days to add" : "Carry over days", type: "number", step: "0.5", value: daysForm.days,
              placeholder: daysForm.mode === "add" ? "e.g. 2 (or -1 to remove)" : "e.g. 5", onChange: (e) => setDaysForm((p) => ({ ...p, days: e.target.value })),
            },
            { type: "custom", render: () => <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>Applies to the {plural(selectedRows.length, "person", "people")} ticked who already have a {daysForm.year} entitlement for this leave type.</p> },
          ]}
        />
      )}

      {exporting && (
        <ExportDialog
          title={`Export ${noun}`}
          filename={exportName}
          scopes={[
            { value: "filtered", label: "Rows shown", rows },
            ...(selectedRows.length ? [{ value: "selected", label: "Ticked rows", rows: selectedRows }] : []),
            { value: "all", label: tab === "requests" ? "All loaded requests" : "Everyone", rows: tab === "requests" ? requests : balances },
          ]}
          defaultScope={exporting}
          columns={tab === "requests" ? requestExportColumns(shownColumns, customFields) : balanceExportColumns(shownColumns, types)}
          groups={Object.entries(groups).map(([value, g]) => ({ value, label: g.label, get: g.get }))}
          defaultGroup={f.groupBy}
          onExported={(count, kind) => showToast(`${plural(count, "row", "rows")} exported${kind === "csv" ? " as CSV" : ""}.`)}
          onClose={() => setExporting(null)}
        />
      )}

      {confirm && <ConfirmDialog message={confirm.message} confirmText={confirm.confirmText} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}
